import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BulkAction, TestInfo, IneligibleTestInfo } from '@/app/features/reports/types/bulk-actions';
import { Loader2 } from 'lucide-react';
import { getStatusDisplayName } from '@/app/features/reports/utils/getStatusDisplayName';

// Helper function to generate dialog content based on action
function getActionDetails(action: BulkAction | null) {
    if (!action) return { title: 'Confirm Action', description: '', confirmText: 'Confirm' };

    switch (action.type) {
        case 'status':
            const status = action.params?.status ?? 'Unknown Status';
            const displayStatus = getStatusDisplayName(status);
            return {
                title: `Confirm Status Change to ${displayStatus}`,
                description: `The status of the selected eligible tests will be changed to ${displayStatus}.`,
                confirmText: `Change to ${displayStatus}`,
                eligibleListTitle: `Tests to be changed to ${displayStatus}:`,
            };
        case 'acceptFastFill':
            // Assuming fast fill acceptance sets status, e.g., to Draft. Adjust as needed.
            const targetStatus = 'Draft'; // Or determine this dynamically if needed
            return {
                title: 'Confirm Accept Fast Fill',
                description: `Eligible tests marked as 'Fast Fill' will be accepted and their status potentially updated (e.g., to ${targetStatus}).`,
                confirmText: 'Accept Fast Fill',
                eligibleListTitle: 'Tests to be Accepted:',
            };
        case 'exclude':
            return {
                title: 'Confirm Exclude Tests',
                description: 'Selected eligible tests will be marked as excluded. This action might be irreversible depending on configuration.',
                confirmText: 'Exclude Tests',
                eligibleListTitle: 'Tests to be Excluded:',
            };
        case 'include':
            return {
                title: 'Confirm Include Tests',
                description: 'Selected eligible tests (currently marked as excluded) will be included in the report.',
                confirmText: 'Include Tests',
                eligibleListTitle: 'Tests to be Included:',
            };
        default:
            // This should only be reached if a new BulkActionType is added
            // without updating this switch statement.
            const unhandledAction: never = action.type;
            console.warn("Unhandled action type in getActionDetails:", unhandledAction);
            return {
                title: 'Confirm Unknown Action',
                description: 'An unknown bulk action is selected.',
                confirmText: 'Confirm',
                eligibleListTitle: 'Eligible Tests:',
            };
    }
}


const BulkStatusChangeDialog = ({
    isOpen,
    onClose,
    eligibleTests,
    ineligibleTests,
    action, // Changed from 'status' to 'action'
    onConfirm,
    isProcessing = false
}: {
    isOpen: boolean;
    onClose: () => void;
    eligibleTests: TestInfo[]; // Use imported type
    ineligibleTests: IneligibleTestInfo[]; // Use imported type
    action: BulkAction | null; // Changed prop type
    onConfirm: () => void;
    isProcessing?: boolean;
}) => {
    // Get dynamic content based on the action
    const { title, description, confirmText, eligibleListTitle } = getActionDetails(action);

    return (
        <Dialog open={isOpen} onOpenChange={isProcessing ? undefined : onClose}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    <DialogDescription>
                        {description} Review the lists below.
                    </DialogDescription>
                </DialogHeader>

                {isProcessing ? (
                    <div className="flex items-center justify-center gap-3 py-8">
                        <Loader2 className="h-5 w-5 animate-spin text-primary" />
                        <span className="text-sm font-medium">Processing...</span>
                    </div>
                ) : (
                    <>
                        {eligibleTests.length > 0 && (
                            <div className="space-y-2">
                                <h3 className="font-medium">{eligibleListTitle}</h3>
                                <div className="max-h-40 overflow-y-auto border rounded p-2">
                                    {eligibleTests.map(test => (
                                        <div key={test.id} className="py-1">
                                            {test.title}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {ineligibleTests.length > 0 && (
                            <div className="space-y-2">
                                <h3 className="font-medium text-red-600">Tests that cannot be changed:</h3>
                                <div className="max-h-40 overflow-y-auto border rounded p-2">
                                    {ineligibleTests.map(test => (
                                        <div key={test.id} className="py-1 flex justify-between">
                                            <span>{test.title}</span>
                                            <span className="text-sm text-muted-foreground">{test.reason}</span> {/* Adjusted styling */}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={isProcessing}>Cancel</Button>
                    <Button onClick={onConfirm} disabled={eligibleTests.length === 0 || !action || isProcessing}>
                        {isProcessing ? (
                            <>
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                Processing...
                            </>
                        ) : (
                            `${confirmText} (${eligibleTests.length} tests)`
                        )}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default BulkStatusChangeDialog;

// It might be better to move these types to a shared types file, e.g., src/app/features/reports/types/bulk-actions.ts
// export interface TestInfo {
//     id: string;
//     title: string;
// }

// export interface IneligibleTestInfo extends TestInfo {
//     reason: string;
// }

// export type BulkActionType = 'status' | 'acceptFastFill'; // Add other action types as needed

// export interface BulkAction {
//     type: BulkActionType;
//     params?: {
//         status?: TestStatus; // Use TestStatus from appropriate import
//         // Add other potential parameters here
//     };
// }